import { useSelector } from "react-redux";
import { useRef, useState, useCallback, ChangeEvent } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { RootState } from "app/store";
import { usePickParentImageAsset } from "app/hook";
import { usePlaySound } from "common/utils";
import { SelectedImage, MessageImagePreview } from "common/elements";
import { StampPicker } from "common/elements/ChatInputBar/StampPicker";
import I18n from "features/parent/i18n";

interface ParentChatInputBarProps {
  onSend: (message: string, imageUrl?: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const ParentChatInputBar = ({
  onSend,
  disabled = false,
  placeholder,
}: ParentChatInputBarProps) => {
  const play = usePlaySound();
  const pickParentImageAsset = usePickParentImageAsset();
  const parent = useSelector((state: RootState) => state.user.parent);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [showStampPicker, setShowStampPicker] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && !selectedImage) return;

    play();
    // textとimageUrlを一緒に渡す（分割しない）
    // タイプ判定は呼び出し元（MessageTab.tsx）で行う
    if (selectedImage) {
      onSend(trimmed, selectedImage.url);
    } else if (trimmed) {
      onSend(trimmed, undefined);
    }
    setValue("");
    setSelectedImage(null);
    setShowStampPicker(false);
  }, [value, selectedImage, onSend, play]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggleStampPicker = () => {
    play();
    setShowStampPicker((prev) => !prev);
  };

  const handleSelectStamp = (stamp: SelectedImage) => {
    setSelectedImage(stamp);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleCloseStampPicker = () => {
    setShowStampPicker(false);
  };

  // 直接画像アップロード
  const handleDirectUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && parent?.uid) {
      pickParentImageAsset(
        file,
        parent.uid,
        (asset) => {
          setSelectedImage({ id: asset.id, url: asset.url, name: file.name });
        },
        () => {
          // エラー時
        },
      );
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isSendDisabled = (!value.trim() && !selectedImage) || disabled;

  return (
    <div className="flex-col-el relative w-full">
      {/* スタンプピッカー */}
      {showStampPicker && (
        <StampPicker onSelect={handleSelectStamp} onClose={handleCloseStampPicker} />
      )}

      {/* 選択画像プレビュー */}
      {selectedImage && <MessageImagePreview image={selectedImage} onRemove={handleRemoveImage} />}

      {/* 入力エリア */}
      <div className="flex-col-el px-4 pt-2 pb-4">
        <div className="flex-row-el items-end gap-3">
          {/* 画像アップロードボタン */}
          <div className="flex-col-el flex-center shrink-0 relative">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleDirectUpload}
              className="absolute inset-0 opacity-0 cursor-pointer z-10"
              disabled={disabled}
            />
            <button
              disabled={disabled}
              className="flex-col-el flex-center cursor-pointer border-2 border-solid active:opacity-70 disabled:opacity-50 rounded-[10px] w-[52px] h-[52px] transition-colors bg-white border-blue-60 hover:bg-blue-20"
            >
              <span className="material-symbols-outlined !text-[28px] text-blue-100">
                photo_camera
              </span>
            </button>
          </div>

          {/* 入力欄 + スタンプボタン内包 */}
          <div className="flex-col-el flex-1 min-w-0">
            <TextField
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowStampPicker(false)}
              placeholder={placeholder || I18n.t("MSG_ASSISTANT_PLACEHOLDER")}
              fullWidth
              multiline
              maxRows={5}
              variant="outlined"
              disabled={disabled}
              InputProps={{
                sx: {
                  borderRadius: 4,
                  paddingX: 3,
                  paddingY: 1,
                  fontSize: 24,
                  fontFamily: "MPLUSRounded1c-Bold",
                  color: "#3F4A61",
                  backgroundColor: "white",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <button
                      onClick={handleToggleStampPicker}
                      disabled={disabled}
                      className={`flex-col-el flex-center cursor-pointer border-none p-1 rounded-lg transition-colors disabled:opacity-50 ${
                        showStampPicker ? "bg-green-100" : "bg-transparent hover:bg-green-20"
                      }`}
                    >
                      <span
                        className={`material-symbols-outlined !text-[28px] ${
                          showStampPicker ? "text-white" : "text-green-100"
                        }`}
                      >
                        sentiment_satisfied
                      </span>
                    </button>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {/* 送信ボタン */}
          <button
            onClick={handleSend}
            disabled={isSendDisabled}
            className="flex-col-el flex-center shrink-0 cursor-pointer border-none active:opacity-70 disabled:bg-gray-20 bg-safe rounded-[10px] p-3"
          >
            <p className="text text-white">{I18n.t("MSG_ASSISTANT_SEND")}</p>
          </button>
        </div>
      </div>
    </div>
  );
};
